import React from 'react';
import { ArrowDownTrayIcon, ClockIcon, PaperAirplaneIcon, PlusIcon } from '@heroicons/react/24/outline';
import { formatRigaSendTime } from '@/utils/report-format';

interface ReportActionBarProps {
  /** LR-257: false → no `send_reports` capability; render read-only (CSV only). */
  canManage: boolean;
  isEditing: boolean;
  isDraftToday: boolean;
  isMutating: boolean;
  isResetting: boolean;
  isSaving: boolean;
  isSending: boolean;
  hasChanges: boolean;
  countdown: { formatted: string; isExpired: boolean } | null;
  autoSendTime: Date | null;
  onDownloadCsv: () => void;
  onEnterEdit: () => void;
  onSendNow: () => void;
  onAddPosition: () => void;
  onReset: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
}

export const ReportActionBar: React.FC<ReportActionBarProps> = ({
  canManage,
  isEditing,
  isDraftToday,
  isMutating,
  isResetting,
  isSaving,
  isSending,
  hasChanges,
  countdown,
  autoSendTime,
  onDownloadCsv,
  onEnterEdit,
  onSendNow,
  onAddPosition,
  onReset,
  onCancelEdit,
  onSave,
}) => {
  // LR-257: without the `send_reports` capability the report is read-only —
  // only the CSV download is offered (matches the sent/historical layout).
  if (!canManage) {
    return (
      <div data-tour="cpty-report-actionbar" className="flex items-center">
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={onDownloadCsv}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5 inline mr-1" />
            CSV
          </button>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div data-tour="cpty-report-actionbar" className="flex items-center gap-3">
        <button
          onClick={onAddPosition}
          disabled={isMutating}
          className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="h-3.5 w-3.5 inline mr-1" />
          Add Position
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={onReset}
            disabled={isMutating}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {isResetting && (
              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {isResetting ? 'Resetting...' : 'Reset'}
          </button>
          <button
            onClick={onCancelEdit}
            disabled={isMutating}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!hasChanges || isMutating}
            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {isSaving && (
              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            Save
          </button>
        </div>
      </div>
    );
  }

  // Sent/historical mode: only CSV
  if (!isDraftToday) {
    return (
      <div data-tour="cpty-report-actionbar" className="flex items-center">
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={onDownloadCsv}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5 inline mr-1" />
            CSV
          </button>
        </div>
      </div>
    );
  }

  // Read mode (draft today, not editing)
  return (
    <div data-tour="cpty-report-actionbar" className="flex items-center">
      <div className="ml-auto flex items-center gap-2">
        {countdown && !countdown.isExpired && (
          <div
            className="flex items-center gap-1.5 text-xs text-gray-500 mr-1"
            title={autoSendTime ? formatRigaSendTime(autoSendTime) : undefined}
          >
            <ClockIcon className="h-4 w-4" />
            <span>Auto-send in {countdown.formatted}</span>
          </div>
        )}
        <button
          onClick={onDownloadCsv}
          className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <ArrowDownTrayIcon className="h-3.5 w-3.5 inline mr-1" />
          CSV
        </button>
        <button
          onClick={onEnterEdit}
          className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          Edit
        </button>
        <button
          onClick={onSendNow}
          disabled={isSending}
          className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          <PaperAirplaneIcon className="h-3.5 w-3.5" />
          {isSending ? 'Sending...' : 'Send Now'}
        </button>
      </div>
    </div>
  );
};
